import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../services/api';

// Check if notifications are supported
const isNotificationSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

export function useNotifications(authData, eventId, onNewRequest = null) {
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);
  const [swRegistration, setSwRegistration] = useState(null);
  const [newRequestsCount, setNewRequestsCount] = useState(0);
  const [lastCheckedRequests, setLastCheckedRequests] = useState([]);
  const pollingIntervalRef = useRef(null);
  const lastCheckTimeRef = useRef(Date.now()); // Track when we last checked
  const notifiedRequestIdsRef = useRef(new Set()); // Track which request IDs we've already notified about

  // Initialize
  useEffect(() => {
    if (isNotificationSupported()) {
      setIsSupported(true);
      setPermission(Notification.permission);
      registerServiceWorker();
    }
  }, []);

  // Register service worker
  const registerServiceWorker = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      setSwRegistration(registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  };

  // Show a local notification (when app is in foreground)
  const showLocalNotification = useCallback((title, body, options = {}) => {
    if (permission !== 'granted') {
      console.log('Notifications not permitted');
      return;
    }

    try {
      // Use service worker notification if available
      if (swRegistration) {
        swRegistration.showNotification(title, {
          body,
          icon: '/images/trempi-logo.png',
          badge: '/images/favicon.png',
          vibrate: [200, 100, 200],
          tag: options.tag || 'trempi-notification',
          data: options.data || {},
          requireInteraction: options.requireInteraction || false,
          ...options
        });
      } else {
        // Fallback to basic notification
        new Notification(title, {
          body,
          icon: '/images/trempi-logo.png',
          tag: options.tag || 'trempi-notification',
          ...options
        });
      }
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }, [permission, swRegistration]);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      console.log('Notifications not supported');
      return 'denied';
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      console.log('Notification permission:', result);
      
      if (result === 'granted') {
        // Show a test notification
        showLocalNotification(
          '🎉 Notifications Enabled!',
          'You will now receive alerts when someone requests to join your ride.',
          { tag: 'permission-granted' }
        );
      }
      
      return result;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }, [isSupported, showLocalNotification]);

  // Check for new join requests to user's offers
  const checkForNewRequests = useCallback(async () => {
    // Get token from authData or localStorage
    const token = authData?.token || localStorage.getItem('authToken');
    if (!token || !eventId) {
      console.log('🔔 Notification check skipped: no token or eventId');
      return;
    }

    console.log('🔔 Checking for new requests...');

    try {
      // Fetch user's offers for this event
      const offersResponse = await fetch(`${API_BASE_URL}/event/${eventId}/offers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!offersResponse.ok) {
        console.log('🔔 Failed to fetch offers:', offersResponse.status);
        return;
      }
      
      const offersData = await offersResponse.json();
      const myOffers = (offersData.offers || []).filter(
        offer => offer.driver_phone === authData?.phone || offer.owner_account_id === authData?.account_id
      );
      
      console.log('🔔 My offers found:', myOffers.length);

      if (myOffers.length === 0) return;

      // Check each offer for pending requests
      let totalNewRequests = 0;
      const allPendingRequests = [];

      for (const offer of myOffers) {
        try {
          const requestsResponse = await fetch(
            `${API_BASE_URL}/carpool/offer/${offer.offer_id}/join-requests`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          if (requestsResponse.ok) {
            const data = await requestsResponse.json();
            const pendingRequests = (data.requests || []).filter(r => r.status === 'pending');
            
            console.log(`🔔 Offer ${offer.offer_id}: ${pendingRequests.length} pending requests`);
            
            // Filter to only requests we haven't notified about yet
            const newRequests = pendingRequests.filter(req => {
              const reqId = req.id || req.join_id || `${offer.offer_id}-${req.name}-${req.created_at}`;
              const alreadyNotified = notifiedRequestIdsRef.current.has(reqId);
              if (!alreadyNotified) {
                console.log(`🔔 NEW request found: ${req.name} (ID: ${reqId})`);
              }
              return !alreadyNotified;
            });

            if (newRequests.length > 0) {
              totalNewRequests += newRequests.length;
              allPendingRequests.push(...newRequests.map(r => ({ ...r, offer })));
            }
          }
        } catch (e) {
          console.error('Error checking requests for offer:', offer.offer_id, e);
        }
      }

      // Update last check time
      lastCheckTimeRef.current = Date.now();

      // Notify about new requests
      if (totalNewRequests > 0) {
        console.log(`🔔 Found ${totalNewRequests} NEW requests to notify about!`);
        setNewRequestsCount(prev => prev + totalNewRequests);
        
        // Show notification for each new request
        allPendingRequests.forEach(req => {
          // Mark this request as notified
          const reqId = req.id || req.join_id || `${req.offer?.offer_id}-${req.name}-${req.created_at}`;
          notifiedRequestIdsRef.current.add(reqId);
          
          const passengerCount = parseInt(req.passenger_count) || 1;
          const message = `${req.name || 'Someone'} wants to join your ride${passengerCount > 1 ? ` (${passengerCount} passengers)` : ''}`;
          
          console.log('🔔 Showing notification:', message, '(ID:', reqId, ')');
          
          // Show browser push notification (only if permission granted)
          if (permission === 'granted') {
            showLocalNotification(
              '🙋 New Ride Request!',
              message,
              {
                tag: `request-${reqId}`,
                data: { eventCode: eventId, offerId: req.offer?.offer_id },
                requireInteraction: true
              }
            );
          }

          // Always call the in-app callback (for toast notifications)
          if (onNewRequest) {
            console.log('🔔 Calling onNewRequest callback');
            onNewRequest(req, message);
          }
        });

        setLastCheckedRequests(allPendingRequests);
      } else {
        console.log('🔔 No new requests to notify about');
      }

    } catch (error) {
      console.error('Error checking for new requests:', error);
    }
  }, [authData, eventId, showLocalNotification, permission, onNewRequest]);

  // Start polling for new requests
  const startPolling = useCallback((intervalMs = 30000) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // Initial check
    checkForNewRequests();
    
    // Set up interval
    pollingIntervalRef.current = setInterval(checkForNewRequests, intervalMs);
    console.log('Started polling for new requests every', intervalMs / 1000, 'seconds');
  }, [checkForNewRequests]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      console.log('Stopped polling for new requests');
    }
  }, []);

  // Clear new requests count
  const clearNewRequestsCount = useCallback(() => {
    setNewRequestsCount(0);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  return {
    isSupported,
    permission,
    requestPermission,
    showLocalNotification,
    startPolling,
    stopPolling,
    newRequestsCount,
    clearNewRequestsCount,
    lastCheckedRequests,
    checkForNewRequests
  };
}

export default useNotifications;
